import { create } from 'zustand';
import { Timestamp } from 'firebase/firestore';
import { JobApplication, ApplicationStatus, DashboardStats } from '../types';
import { applicationService, CreateApplicationData, UpdateApplicationData } from '../services/applicationService';
import { useAuthStore } from './authStore';

interface ApplicationStore {
  applications: JobApplication[];
  stats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  
  // Actions
  setApplications: (applications: JobApplication[]) => void;
  addApplication: (application: JobApplication) => void;
  updateApplication: (id: string, updates: Partial<JobApplication>) => void;
  deleteApplication: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  calculateStats: () => void;
  
  // Firestore actions
  loadApplications: (userId: string) => Promise<void>;
  createApplication: (userId: string, data: CreateApplicationData) => Promise<JobApplication>;
  updateApplicationInFirestore: (userId: string, id: string, data: UpdateApplicationData) => Promise<void>;
  deleteApplicationFromFirestore: (userId: string, id: string) => Promise<void>;
  updateApplicationStatus: (userId: string, id: string, status: ApplicationStatus, note?: string) => Promise<void>;
  syncWithFirestore: (userId: string) => Promise<void>;
}

export const useApplicationStore = create<ApplicationStore>((set, get) => ({
  applications: [],
  stats: null,
  loading: false,
  error: null,
  initialized: false,

  setApplications: (applications) => {
    set({ applications, initialized: true });
    get().calculateStats();
  },

  addApplication: (application) => {
    set((state) => ({ 
      applications: [...state.applications, application] 
    }));
    get().calculateStats();
  },

  updateApplication: (id, updates) => {
    set((state) => ({
      applications: state.applications.map(app => 
        app.id === id ? { ...app, ...updates } : app
      )
    }));
    get().calculateStats();
  },

  deleteApplication: (id) => {
    set((state) => ({
      applications: state.applications.filter(app => app.id !== id)
    }));
    get().calculateStats();
  },

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  calculateStats: () => {
    const { applications } = get();
    
    const statusCounts = applications.reduce((acc, app) => {
      acc[app.status] = (acc[app.status] || 0) + 1;
      return acc;
    }, {} as Record<ApplicationStatus, number>);

    // Ensure all statuses are represented
    const allStatuses: ApplicationStatus[] = ['In attesa', 'Colloquio programmato', 'Accettato', 'Rifiutato', 'Scaduto'];
    allStatuses.forEach(status => {
      if (!statusCounts[status]) {
        statusCounts[status] = 0;
      }
    });

    const recentActivity = applications
      .sort((a, b) => b.appliedDate.seconds - a.appliedDate.seconds)
      .slice(0, 5);

    const stats: DashboardStats = {
      totalApplications: applications.length,
      statusCounts,
      recentActivity
    };

    set({ stats });
  },

  // Carica le candidature da Firestore
  loadApplications: async (userId: string) => {
    try {
      set({ loading: true, error: null });
      console.log('🔄 Caricamento candidature da Firestore per utente:', userId);
      
      const applications = await applicationService.getUserApplications(userId);
      set({ applications, loading: false, initialized: true });
      get().calculateStats();
      
      console.log(`✅ Caricate ${applications.length} candidature da Firestore`);
    } catch (error) {
      console.error('❌ Errore nel caricamento candidature:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Errore nel caricamento candidature',
        loading: false 
      });
    }
  },

  // Crea una nuova candidatura in Firestore
  createApplication: async (userId: string, data: CreateApplicationData) => {
    try {
      set({ loading: true, error: null });
      console.log('📝 Creazione candidatura in Firestore...');
      
      const newApplication = await applicationService.createApplication(userId, data);
      
      set((state) => ({
        applications: [newApplication, ...state.applications],
        loading: false
      }));
      
      get().calculateStats();
      console.log('✅ Candidatura creata in Firestore');
      
      return newApplication;
    } catch (error) {
      console.error('❌ Errore nella creazione candidatura:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Errore nella creazione candidatura',
        loading: false 
      });
      throw error;
    }
  },

  // Aggiorna una candidatura in Firestore
  updateApplicationInFirestore: async (userId: string, id: string, data: UpdateApplicationData) => {
    try {
      set({ loading: true, error: null });
      console.log('📝 Aggiornamento candidatura in Firestore:', id);
      
      await applicationService.updateApplication(userId, id, data);
      
      set((state) => ({
        applications: state.applications.map(app => 
          app.id === id ? { 
            ...app, 
            ...data,
            appliedDate: data.appliedDate ? Timestamp.fromDate(data.appliedDate) : app.appliedDate
          } : app
        ),
        loading: false
      }));
      
      get().calculateStats();
      console.log('✅ Candidatura aggiornata in Firestore');
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento candidatura:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Errore nell\'aggiornamento candidatura',
        loading: false 
      });
      throw error;
    }
  },

  // Elimina una candidatura da Firestore
  deleteApplicationFromFirestore: async (userId: string, id: string) => {
    try {
      set({ loading: true, error: null });
      console.log('🗑️ Eliminazione candidatura da Firestore:', id);
      
      await applicationService.deleteApplication(userId, id);
      
      set((state) => ({
        applications: state.applications.filter(app => app.id !== id),
        loading: false
      }));
      
      get().calculateStats();
      console.log('✅ Candidatura eliminata da Firestore');
    } catch (error) {
      console.error('❌ Errore nell\'eliminazione candidatura:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Errore nell\'eliminazione candidatura',
        loading: false 
      });
      throw error;
    }
  },

  // Aggiorna lo stato di una candidatura in Firestore
  updateApplicationStatus: async (userId: string, id: string, status: ApplicationStatus, note?: string) => {
    try {
      set({ loading: true, error: null });
      console.log('📝 Aggiornamento stato candidatura in Firestore:', id, '→', status);
      
      await applicationService.updateApplicationStatus(userId, id, status, note);
      
      set((state) => ({
        applications: state.applications.map(app => 
          app.id === id ? { 
            ...app, 
            status,
            statusHistory: [
              ...app.statusHistory,
              {
                status,
                timestamp: Timestamp.now(),
                note: note || `Stato aggiornato a: ${status}`
              }
            ]
          } : app
        ),
        loading: false
      }));
      
      get().calculateStats();
      console.log('✅ Stato candidatura aggiornato in Firestore');
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento stato:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Errore nell\'aggiornamento stato',
        loading: false 
      });
      throw error;
    }
  },

  // Sincronizza le candidature locali con Firestore
  syncWithFirestore: async (userId: string) => {
    try {
      set({ loading: true, error: null });
      console.log('🔄 Sincronizzazione con Firestore...');
      
      const { applications } = get();
      const syncedApplications = await applicationService.syncApplications(userId, applications);
      
      set({ 
        applications: syncedApplications,
        loading: false,
        initialized: true
      });
      
      get().calculateStats();
      console.log('✅ Sincronizzazione completata');
    } catch (error) {
      console.error('❌ Errore nella sincronizzazione:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Errore nella sincronizzazione',
        loading: false 
      });
    }
  }
})); 